import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { getVendingMachine } from '../../actions/vendingMachine';
import { bulkAddToStock, addContainers, getContainers } from '../../actions/inventory';
import Container from './Container';
import { stockUpdate } from '../../actions/test';
import { 
    ArrowLeft, 
    Package, 
    Plus, 
    RefreshCw, 
    Filter,
    CheckCircle,
    XCircle,
    DoorOpen,
    Trash2,
    Box,
    Settings,
    AlertTriangle,
    Layers,
    ShoppingCart,
    Building
} from 'lucide-react';

const Inventory = ({
    vendingMachine,
    getVendingMachine,
    bulkAddToStock,
    addContainers,
    loading,
    getContainers,
    containerinfo,
    brandsData,
    containersData,
    vmId,
    stockUpdate
}) => {
    const { id } = useParams();
    const [maxContainers, setMaxContainers] = useState(0);
    const [isNewInventory, setInventoryType] = useState(true);
    const [containers, setContainers] = useState([]);
    const [brands, setBrands] = useState({});
    const [vendingMachineId, setVendingMachineId] = useState("");
    const [stockUpdateContainers, setStockUpdateContainers] = useState([]);
    const [emptyContainers, setEmptyContainers] = useState([]);
    const [formData, setBrandSelected] = useState({ brandSelected: "" });
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedCount, setSelectedCount] = useState(0);

    useEffect(() => {
        if (!vendingMachine) {
            getVendingMachine(id);
            getContainers(id);
        } else if (vendingMachine._id !== id) {
            getVendingMachine(id);
            getContainers(id);
        } else if (vendingMachineId !== id) {
            getContainers(id);
        }
        
        if (containersData?.length) {
            setInventoryType(false);
        } else {
            setInventoryType(true);
        }
        
        // Set vending machine id and brands
        if (vendingMachineId !== id) {
            setVendingMachineId(id);
        }
        
        if (containersData) {
            setBrands({ ...brandsData });
            setContainers([...containersData]);
            
            let emptyContainers = containersData.filter((container) => {
                return (container.isWorking && !container.isReserved && container.isEmpty && container.product != null) === true;
            });
            emptyContainers = emptyContainers.map(container => ({ ...container, selected: false }));
            setEmptyContainers(emptyContainers);
            setStockUpdateContainers(emptyContainers);
        }
    }, [containersData, vendingMachine, id, vendingMachineId, brandsData]);

    useEffect(() => {
        const count = stockUpdateContainers.filter(container => container.selected).length;
        setSelectedCount(count);
    }, [stockUpdateContainers]);

    const populateContainers = () => {
        if (vendingMachineId !== id) {
            return null;
        }
        return containers.map((container, index) => (
            <Container key={index} container={container} brands={brands} />
        ));
    };

    const populateContainersForStockUpdate = () => {
        if (stockUpdateContainers.length === 0) {
            return (
                <div className="col-12 text-center py-5">
                    <Package size={64} className="text-muted mb-3" />
                    <h5 className="text-muted">No Empty Containers</h5>
                    <p className="text-muted">All containers are either occupied or not working</p>
                </div>
            );
        }

        return stockUpdateContainers.map((container, index) => (
            <div 
                key={index} 
                className="col-xl-3 col-lg-4 col-md-6 mb-4" 
                style={{ cursor: 'pointer' }}
                onClick={() => selectContainersForStockUpdate(index)}
            >
                <div className={`card shadow-sm h-100 border-2 ${container.selected ? 'border-success bg-success bg-opacity-10' : 'border-light'} transition-all`}>
                    <div className="card-header bg-light text-center">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold">Container {container.containerId}</span>
                            {container.selected && <CheckCircle size={20} className="text-success" />}
                        </div>
                    </div>
                    <div className="card-body text-center">
                        <Box size={48} className="text-primary mb-3" />
                        <h6 className="card-title">{brands[container.product]}</h6>
                        <span className="badge bg-warning">Empty</span>
                    </div>
                    <div className="card-footer bg-transparent">
                        <small className="text-muted">Click to select for bulk operations</small>
                    </div>
                </div>
            </div>
        ));
    };

    const selectContainersForStockUpdate = (index) => {
        const containersList = [...stockUpdateContainers];
        containersList[index].selected = !containersList[index].selected;
        setStockUpdateContainers(containersList);
    };

    const onChange = (e) => {
        setMaxContainers(e.target.value);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (maxContainers) {
            setIsProcessing(true);
            try {
                let containerBody = { vendingMachine: vendingMachine._id, containers: maxContainers };
                await addContainers(containerBody);
            } finally {
                setIsProcessing(false);
            }
        }
    };

    const addNewContainer = async () => {
        setIsProcessing(true);
        try {
            let containerBody = { vendingMachine: vendingMachine._id, containers: 1 };
            await addContainers(containerBody);
        } finally {
            setIsProcessing(false);
        }
    };

    const clearSelectedContainers = () => {
        let containersList = [...stockUpdateContainers];
        containersList = containersList.map(container => ({ ...container, selected: false }));
        setStockUpdateContainers(containersList);
    };

    const addToStockBulk = async () => {
        setIsProcessing(true);
        try {
            let data = { containers: [], vendingMachine: vendingMachine._id };
            stockUpdateContainers.forEach((container) => {
                if (container.selected) {
                    data.containers.push(container._id);
                }
            });
            await bulkAddToStock(data);
        } finally {
            setIsProcessing(false);
        }
    };

    const filterBrand = (e) => {
        setBrandSelected({ ...formData, [e.target.name]: e.target.value });
        let filteredData = emptyContainers.filter((container) => {
            if (e.target.value === "all") {
                return true;
            }
            return container.product === e.target.value;
        });
        setStockUpdateContainers(filteredData);
    };

    const openForStockUpdate = async () => {
        setIsProcessing(true);
        try {
            let data = { vendingMachineId: vendingMachine.vendingMachineId, containers: [] };
            stockUpdateContainers.forEach((container) => {
                if (container.selected) {
                    data.containers.push(container.containerId);
                }
            });
            await stockUpdate(data);
        } finally {
            setIsProcessing(false);
        }
    };

    const populateBrands = () => {
        if (!brands) {
            return [];
        }
        return Object.keys(brands)
            .filter(key => brands[key] !== "Refundable Deposit")
            .map((key) => (
                <option key={`brand-${key}`} value={key}>
                    {brands[key]}
                </option>
            ));
    };

    const { brandSelected } = formData;

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <Link 
                                    className="btn btn-outline-primary d-flex align-items-center mb-2" 
                                    to={`/inventory/v1.2/vendingMachine/${vendingMachine ? vendingMachine._id : ""}`}
                                >
                                    <ArrowLeft size={16} className="me-2" />
                                    Back to Machine Details
                                </Link>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold d-flex align-items-center">
                                    <Package size={32} className="me-3 text-primary" />
                                    Inventory Management
                                </h1>
                                <p className="text-muted mb-0">
                                    {vendingMachine ? `Managing containers for ${vendingMachine.name}` : 'Loading machine details...'}
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {loading && (
                                    <div className="d-flex align-items-center text-muted">
                                        <RefreshCw size={16} className="me-2 spinner-border spinner-border-sm" />
                                        Loading...
                                    </div>
                                )}
                                <span className="badge bg-primary fs-6">
                                    {containers.length} Container{containers.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* New Inventory Setup */}
                {isNewInventory && (
                    <div className="row mb-5">
                        <div className="col-lg-6 mx-auto">
                            <div className="card shadow-sm">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Settings size={20} className="me-2" />
                                        Initial Setup
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="alert alert-info d-flex align-items-start">
                                        <AlertTriangle size={20} className="me-2 mt-1 flex-shrink-0" />
                                        <div>
                                            <strong>First Time Setup:</strong> Please specify the maximum number of containers for this vending machine.
                                        </div>
                                    </div>
                                    <form onSubmit={onSubmit}>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">
                                                <Layers size={16} className="me-2" />
                                                Maximum Containers
                                            </label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                placeholder="Enter maximum number of containers"
                                                name="maxContainers"
                                                value={maxContainers}
                                                onChange={onChange}
                                                min="1"
                                                max="50"
                                                required
                                            />
                                            <div className="form-text">
                                                Recommended range: 10-30 containers depending on machine size
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary btn-lg"
                                                disabled={isProcessing || !maxContainers}
                                            >
                                                {isProcessing ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Creating Containers...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Plus size={20} className="me-2" />
                                                        Create {maxContainers} Containers
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Existing Inventory Management */}
                {!isNewInventory && (
                    <>
                        {/* Container Management Section */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card shadow-sm">
                                    <div className="card-header bg-white border-bottom">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0 d-flex align-items-center">
                                                <Box size={20} className="me-2 text-primary" />
                                                Container Management
                                            </h5>
                                            <button 
                                                className="btn btn-success"
                                                onClick={addNewContainer}
                                                disabled={isProcessing}
                                            >
                                                {isProcessing ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Plus size={16} className="me-2" />
                                                        Add Container
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            {containers.length ? populateContainers() : (
                                                <div className="col-12 text-center py-5">
                                                    <Package size={64} className="text-muted mb-3" />
                                                    <h5 className="text-muted">No Containers Found</h5>
                                                    <p className="text-muted">Start by adding your first container</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stock Update Section */}
                        <div className="row">
                            <div className="col-12">
                                <div className="card shadow-sm">
                                    <div className="card-header bg-light">
                                        <h5 className="mb-0 d-flex align-items-center">
                                            <ShoppingCart size={20} className="me-2 text-primary" />
                                            Bulk Stock Management
                                            {selectedCount > 0 && (
                                                <span className="badge bg-success ms-2">{selectedCount} selected</span>
                                            )}
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {/* Filter and Action Bar */}
                                        <div className="row mb-4">
                                            <div className="col-lg-6">
                                                <label className="form-label fw-bold">
                                                    <Filter size={16} className="me-2" />
                                                    Filter by Product
                                                </label>
                                                <select
                                                    className="form-select"
                                                    value={brandSelected || ""}
                                                    name="brandSelected"
                                                    onChange={filterBrand}
                                                >
                                                    <option value="all">🌐 All Products</option>
                                                    {populateBrands()}
                                                </select>
                                            </div>
                                            <div className="col-lg-6">
                                                <label className="form-label fw-bold">Actions</label>
                                                <div className="d-flex gap-2">
                                                    <button 
                                                        className="btn btn-primary"
                                                        onClick={openForStockUpdate}
                                                        disabled={selectedCount === 0 || isProcessing}
                                                    >
                                                        <DoorOpen size={16} className="me-1" />
                                                        Open ({selectedCount})
                                                    </button>
                                                    <button 
                                                        className="btn btn-success"
                                                        onClick={addToStockBulk}
                                                        disabled={selectedCount === 0 || isProcessing}
                                                    >
                                                        <Plus size={16} className="me-1" />
                                                        Add Stock ({selectedCount})
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-secondary"
                                                        onClick={clearSelectedContainers}
                                                        disabled={selectedCount === 0}
                                                    >
                                                        <XCircle size={16} className="me-1" />
                                                        Clear
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Empty Containers Grid */}
                                        <div className="row">
                                            {populateContainersForStockUpdate()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Fragment>
    );
};

Inventory.propTypes = {
    vendingMachine: PropTypes.object,
    getVendingMachine: PropTypes.func.isRequired,
    bulkAddToStock: PropTypes.func.isRequired,
    addContainers: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    getContainers: PropTypes.func.isRequired,
    containerinfo: PropTypes.object,
    brandsData: PropTypes.object,
    containersData: PropTypes.array,
    vmId: PropTypes.string,
    stockUpdate: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    vendingMachine: state.vendingMachine.vendingMachine,
    loading: state.vendingMachine.loading,
    containerinfo: state.inventory.containerinfo,
    brandsData: state.inventory.brandsData,
    containersData: state.inventory.containersData,
    vmId: state.inventory.vmId
});

export default connect(
    mapStateToProps, 
    { bulkAddToStock, getContainers, addContainers, getVendingMachine, stockUpdate }
)(Inventory);

